import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslations } from "use-intl";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import DropZone from "@/components/upload/DropZone";
import UploadProgress, { FileUploadStatus } from "@/components/upload/UploadProgress";
import DraftList, { RejectedSection, NeedsReviewSection } from "@/components/upload/DraftList";
import DraftDetailDialog from "@/components/upload/DraftDetailDialog";
import CompletedDocumentDialog from "@/components/upload/CompletedDocumentDialog";
import RejectedDocumentCompareDialog from "@/components/upload/RejectedDocumentCompareDialog";
import ReviewDetailsModal from "@/components/review/ReviewDetailsModal";
import { InvoiceUploadService } from "@/services/invoiceUploadService";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { InvoiceApiClient } from "@/clients/invoiceApiClient";
import { ReviewApiClient } from "@/clients/reviewApiClient";
import { Invoice } from "@/types/payments";
import { ReviewItem } from "@/types/review";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatAmount } from "@financialharmony/ui";
import { retryBlockedSummary } from "@/components/upload/retryBlockedSummary";
import { useFriendlyError } from "@/i18n/useFriendlyError";

type UploadContext = "business" | "personal";

const Upload: React.FC = () => {
  const t = useTranslations("consultantUpload.page");
  const { friendlyError } = useFriendlyError();
  const [uploadContext, setUploadContext] = useState<UploadContext | null>(null);
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Draft lists
  const [processingDrafts, setProcessingDrafts] = useState<Invoice[]>([]);
  const [needsInputDrafts, setNeedsInputDrafts] = useState<Invoice[]>([]);
  const [finalizedDocs, setFinalizedDocs] = useState<Invoice[]>([]); // already finalized documents
  const [draftsLoading, setDraftsLoading] = useState(true);

  // Dialog state
  const [selectedDraft, setSelectedDraft] = useState<Invoice | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Completed document dialog state
  const [completedDoc, setCompletedDoc] = useState<Invoice | null>(null);
  const [completedDialogOpen, setCompletedDialogOpen] = useState(false);

  // Truly rejected documents (duplicate, poor quality, etc.)
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [rejectedDocs, setRejectedDocs] = useState<Invoice[]>([]);
  // F2 (product-feedback-30-aug.AC2, issue #904): the tab badge shows unseen count only —
  // the list itself (rejectedDocs above) always shows full history regardless of seen state.
  const [unseenRejectedCount, setUnseenRejectedCount] = useState(0);
  const [compareDocId, setCompareDocId] = useState<string | null>(null);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);

  // Documents routed to manual review (unsupported type)
  const [manualReviewItems, setManualReviewItems] = useState<ReviewItem[]>([]);

  // Manual review modal
  const [manualReviewItem, setManualReviewItem] = useState<ReviewItem | null>(null);
  const [manualReviewModalOpen, setManualReviewModalOpen] = useState(false);

  const uploadServiceRef = useRef(new InvoiceUploadService());

  // Polls while documents are processing, and for one tick after the last one leaves (see refresh).
  const [shouldPoll, setShouldPoll] = useState(false);
  const sawProcessingRef = useRef(false);

  // The fetchers below set state only inside promise callbacks, never synchronously, so the mount
  // effect can call them (react-hooks/set-state-in-effect).

  // Fetch drafts by status. Resolves whether any draft is still processing, or null if the read failed.
  const fetchDrafts = useCallback(
    (): Promise<boolean | null> =>
      Promise.all([
        InvoiceApiClient.getDraftsByStatus("pending"),
        InvoiceApiClient.getDraftsByStatus("failed"),
        InvoiceApiClient.getDraftsByStatus("partial"),
        InvoiceApiClient.getDraftsByStatus("completed"),
        InvoiceApiClient.getRecentlyFinalized(50),
      ])
        .then(([pending, failed, partial, completed, finalized]) => {
          const byCreatedDesc = (a: Invoice, b: Invoice) =>
            (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
          const processingList = pending.invoices.slice().sort(byCreatedDesc);
          // Drafts with completed extraction that failed auto-finalization also need manual input
          const needsInputList = [...failed.invoices, ...partial.invoices, ...completed.invoices].sort(byCreatedDesc);
          setProcessingDrafts(processingList);
          setNeedsInputDrafts(needsInputList);
          setFinalizedDocs(finalized.invoices.slice().sort(byCreatedDesc));
          return processingList.length > 0;
        })
        .catch((error) => {
          console.error("Failed to fetch drafts:", error);
          return null;
        })
        .finally(() => setDraftsLoading(false)),
    [],
  );

  // Fetch companyId once on mount
  useEffect(() => {
    supabase.rpc('current_company_id').then(({ data, error }) => {
      if (!error && data) setCompanyId(data);
    });
  }, []);

  const fetchRejected = useCallback((): Promise<void> => {
    if (!companyId) return Promise.resolve();
    return ServiceRegistry.getInvoiceUploadService()
      .getRejectedDocuments(companyId)
      .then(setRejectedDocs)
      .catch((error) => {
        console.error('Failed to load rejected documents:', error);
        toast.error(t("rejectedLoadFailed"));
      });
  }, [companyId]);

  const fetchUnseenRejectedCount = useCallback((): Promise<void> => {
    if (!companyId) return Promise.resolve();
    return ServiceRegistry.getInvoiceUploadService()
      .getUnseenRejectedCount(companyId)
      .then(setUnseenRejectedCount)
      .catch((error) => {
        console.error('Failed to load unseen rejected count:', error);
      });
  }, [companyId]);

  const fetchManualReviewItems = useCallback(
    (): Promise<void> =>
      ReviewApiClient.getNeedsManualReviewItems()
        .then(({ items }) => setManualReviewItems(items))
        .catch((error) => {
          console.error('Failed to load manual review items:', error);
        }),
    [],
  );

  // Every list on the page. The drafts are read first and the other lists only after, so the
  // Rejected and Needs Review reads are never older than the read that found Processing empty.
  const refresh = useCallback(
    () =>
      fetchDrafts().then((processing) =>
        Promise.all([fetchRejected(), fetchUnseenRejectedCount(), fetchManualReviewItems()]).then(() => {
          if (processing === null) return;
          // One more tick after Processing empties: a job can move a document through two writes
          // (extracted, then finalized or rejected as a duplicate), and a read between them must
          // not be the page's last.
          setShouldPoll(processing || sawProcessingRef.current);
          sawProcessingRef.current = processing;
        }),
      ),
    [fetchDrafts, fetchRejected, fetchUnseenRejectedCount, fetchManualReviewItems],
  );

  // On mount (again once the company id resolves), then every 10s while polling.
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!shouldPoll) return;
    const interval = setInterval(refresh, 10000);
    return () => clearInterval(interval);
  }, [shouldPoll, refresh]);

  const [retryingFinalizations, setRetryingFinalizations] = useState(false);

  // FIN-1637: every draft in needsInputDrafts with extractionStatus 'completed' already failed
  // finalizeDraftInvoice once -- see DraftDetailDialog's "Auto-finalization failed" state.
  const failedFinalizationCount = needsInputDrafts.filter((d) => d.extractionStatus === "completed").length;

  const handleRetryFailedFinalizations = async () => {
    try {
      setRetryingFinalizations(true);
      const result = await InvoiceApiClient.retryFailedFinalizations();
      // Why each draft is still blocked, so nobody has to open them one by one to find out.
      const blockedLines = retryBlockedSummary(result.stillBlocked, {
        line: (document, reason) => t("retryBlockedLine", { document, reason }),
        unnamed: t("retryBlockedUnnamed"),
        more: (count) => t("retryBlockedMore", { count }),
        reason: friendlyError,
      });
      const description = blockedLines.length > 0
        ? <div data-testid="retry-blocked-reasons">{blockedLines.map((line, index) => <p key={`${index}-${line}`}>{line}</p>)}</div>
        : undefined;
      if (result.succeeded > 0) {
        toast.success(t("retryFinalized", { succeeded: String(result.succeeded), attempted: String(result.attempted) }), { description });
      } else {
        toast.info(t("retryNone", { attempted: String(result.attempted) }), { description });
      }
      await refresh();
    } catch (error) {
      console.error("Failed to retry finalizations:", error);
      toast.error(t("retryFailed"));
    } finally {
      setRetryingFinalizations(false);
    }
  };

  const handleDeleteDraft = async (draftId: string) => {
    try {
      const service = ServiceRegistry.getInvoiceUploadService();
      await service.deleteDocument(draftId);
      await refresh();
    } catch (error) {
      console.error('Failed to delete draft:', error);
      toast.error(t("deleteFailed"));
    }
  };

  const handleViewRejected = (documentId: string) => {
    setCompareDocId(documentId);
    setCompareDialogOpen(true);
  };

  const handleDraftClick = (draft: Invoice) => {
    setSelectedDraft(draft);
    setDialogOpen(true);
  };

  const handleFinalizedClick = (doc: Invoice) => {
    setCompletedDoc(doc);
    setCompletedDialogOpen(true);
  };

  const handleFinalized = () => {
    refresh();
  };

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (!uploadContext) {
        toast.error(t("selectContext"));
        return;
      }

      const documentType = uploadContext === 'personal' ? 'expense' : 'unknown';

      setIsUploading(true);

      // Initialize file statuses
      const initialStatuses: FileUploadStatus[] = files.map((f) => ({
        name: f.name,
        status: "uploading",
      }));
      setFileStatuses(initialStatuses);

      const onProgress = (index: number, status: FileUploadStatus["status"], error?: string) => {
        setFileStatuses((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], status, error };
          return updated;
        });
      };

      try {
        const results = await uploadServiceRef.current.uploadMultipleFiles(
          files,
          documentType,
          onProgress
        );

        const successCount = results.filter((r) => "draftId" in r).length;
        const errorCount = results.filter((r) => "error" in r).length;

        if (successCount > 0 && errorCount === 0) {
          toast.success(t("allUploaded", { count: String(successCount) }));
        } else if (successCount > 0) {
          toast.warning(t("partialUploaded", { uploaded: String(successCount), failed: String(errorCount) }));
        } else {
          toast.error(t("allFailed"));
        }

        // The new uploads are processing even if the job has already run by the time the read
        // answers, so keep polling for at least one more tick either way.
        sawProcessingRef.current = true;
        setShouldPoll(true);
        refresh();
      } catch (error) {
        console.error("Bulk upload failed:", error);
        toast.error(t("uploadFailed"));
      } finally {
        setIsUploading(false);
      }
    },
    [uploadContext, refresh, t]
  );

  const handleManualReview = (item: ReviewItem) => {
    setManualReviewItem(item);
    setManualReviewModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
      <p className="text-muted-foreground">
        {t("subtitle")}
      </p>

      <Tabs defaultValue="upload">
        <TabsList>
          <TabsTrigger value="upload">{t("tabs.upload")}</TabsTrigger>
          <TabsTrigger value="processing">
            {t("tabs.processing")}
            {processingDrafts.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {processingDrafts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="needs-input">
            {t("tabs.needsInput")}
            {needsInputDrafts.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {needsInputDrafts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="needs-review">
            {t("tabs.needsReview")}
            {manualReviewItems.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {manualReviewItems.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed">
            {t("tabs.completed")}
            {finalizedDocs.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {finalizedDocs.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rejected">
            {t("tabs.rejected")}
            {unseenRejectedCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {unseenRejectedCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-6">
          {/* Upload context selector */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">{t("documentType")}</Label>
            <RadioGroup
              value={uploadContext ?? ""}
              onValueChange={(val) => setUploadContext(val as UploadContext)}
              className="flex flex-col space-y-1"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="business" id="ctx-business" />
                <Label htmlFor="ctx-business" className="font-normal">
                  {t("business")}
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="personal" id="ctx-personal" />
                <Label htmlFor="ctx-personal" className="font-normal">
                  {t("personal")}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Drop zone */}
          <DropZone
            onFilesSelected={handleFilesSelected}
            disabled={isUploading || !uploadContext}
          />

          {/* Upload progress */}
          {fileStatuses.length > 0 && (
            <UploadProgress files={fileStatuses} />
          )}
        </TabsContent>

        <TabsContent value="processing">
          <DraftList
            drafts={processingDrafts}
            loading={draftsLoading}
            mode="processing"
          />
        </TabsContent>

        <TabsContent value="needs-input" className="space-y-4">
          {failedFinalizationCount > 0 && (
            <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-4">
              <p className="text-sm text-muted-foreground">{t("failedFinalization", { count: failedFinalizationCount })}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetryFailedFinalizations}
                disabled={retryingFinalizations}
              >
                {retryingFinalizations ? t("retrying") : t("retryAll")}
              </Button>
            </div>
          )}
          <DraftList
            drafts={needsInputDrafts}
            loading={draftsLoading}
            mode="needs-input"
            onDraftClick={handleDraftClick}
            onDelete={handleDeleteDraft}
          />
        </TabsContent>

        <TabsContent value="needs-review">
          {manualReviewItems.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t("needsReviewEmpty")}</p>
          ) : (
            <NeedsReviewSection
              items={manualReviewItems}
              onHandleManually={handleManualReview}
            />
          )}
        </TabsContent>

        <TabsContent value="completed">
          {finalizedDocs.length > 0 ? (
            <DraftList
              drafts={finalizedDocs}
              loading={draftsLoading}
              mode="finalized"
              onFinalizedClick={handleFinalizedClick}
            />
          ) : (
            !draftsLoading && (
              <p className="text-muted-foreground text-center py-8">{t("completedEmpty")}</p>
            )
          )}
        </TabsContent>

        <TabsContent value="rejected">
          {rejectedDocs.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">{t("rejectedEmpty")}</p>
          ) : (
            <RejectedSection
              rejected={rejectedDocs}
              onView={handleViewRejected}
              onMarkedSeen={fetchUnseenRejectedCount}
            />
          )}
        </TabsContent>
      </Tabs>

      <DraftDetailDialog
        draft={selectedDraft}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onFinalized={handleFinalized}
        onDeleted={refresh}
      />

      <CompletedDocumentDialog
        document={completedDoc}
        open={completedDialogOpen}
        onOpenChange={setCompletedDialogOpen}
      />

      {companyId && (
        <RejectedDocumentCompareDialog
          documentId={compareDocId}
          companyId={companyId}
          open={compareDialogOpen}
          onOpenChange={setCompareDialogOpen}
        />
      )}

      <ReviewDetailsModal
        open={manualReviewModalOpen}
        onOpenChange={setManualReviewModalOpen}
        item={manualReviewItem}
        formatCurrency={(amount, currency) =>
          formatAmount(amount, currency)
        }
        onReviewComplete={() => {
          fetchManualReviewItems();
          setManualReviewItem(null);
        }}
      />
    </div>
  );
};

export default Upload;
